import { useNavigate } from "react-router-dom";
import { setNonEligible } from "../../../stateManagement/actions/claims";
import "./ApprovalModal.css";
import Modal from "@mui/material/Modal";
import InfoIconImgSrc from "../../../assets/image/png/info.png";
import TickconImgSrc from "../../../assets/image/png/tick.png";
import { resetClaimsState } from "../../../stateManagement/actions/claims";
import { setSelectedOpenClaimsRow } from "../../../stateManagement/actions/claims";
import { postClaim } from "../../../services/claimService";
import { getLastNPathSegments} from "../utils/utils";
import { useState } from "react";
import { useDispatch } from "react-redux";
import RenderTooltipContent from "../../../components/dms_common/RenderTooltipContent";
import { Tooltip } from "@mui/material";
import { formatDecimal } from "../../../components/dms_common/DmsUtils";
import { logAnalyticsEvent } from "../../../../src/firebase";

interface ApprovalModalProps {
  approvalModalOpen: boolean;
  setApprovalModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  approvalModalState: number;
  setApprovalModalState: React.Dispatch<React.SetStateAction<number>>;
  postBody: { [key: string]: string | number | any };
  isOpenClaimsPage?: boolean;
  openClaimsRows?: any[];
  selectedOpenClaimsRow?: any;
  updateSelectedRowInRows?: (row: any) => void;
  claimType?: string;
  claimSubtype?: string;
  setIsModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

const approvalModalText: any = {
  1: "Are you sure you want to submit this for approval?",
  2: "Your changes have been saved.",
};

const ApprovalModal: React.FC<ApprovalModalProps> = ({
  approvalModalOpen,
  setApprovalModalOpen,
  approvalModalState,
  setApprovalModalState,
  postBody,
  isOpenClaimsPage = false,
  openClaimsRows,
  selectedOpenClaimsRow,
  updateSelectedRowInRows,
  claimType,
  claimSubtype,
  setIsModalOpen,
}) => {
  const [errorMessage, setErrorMessage] = useState<React.ReactNode>(null);
  const [errorStatusCode, setErrorStatusCode] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false); 
  const [successMessage, setSuccessMessage] = useState<React.ReactNode>(approvalModalText[2] ?? "");
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const { currency, claimTypes } = ConfigDictionary;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const path = getLastNPathSegments(window.location.href, 2);
  const lastSegment = path[0];

  const handleClose = () => {
    setApprovalModalState(1);
    setApprovalModalOpen(false);
    setErrorMessage(null);
    if (approvalModalState === 2) {
      navigate("/dashboard/claims/status");
      dispatch(resetClaimsState());
    }
  };
  const formatErrorMessage = (message: string): React.ReactNode => {
    try {
      const normalizedMessage = message
        .replace(/\s*:\s*/g, ':')    
        .replace(/\s*=\s*/g, '=')   
        .replace(/\s*,\s*/g, ',')   
        .replace(/\s*\[\s*/g, '[')   
        .replace(/\s*\]\s*/g, ']'); 
  
      const parts = normalizedMessage.split('\n\n');
      const errorLines = parts[0].split('\n').filter(line => line.trim() !== '');
      const skuDetails = parts[1] || '';
  
      const getAggregationDisplay = (mode: string): string => {
        const modeMap: Record<string, string> = {
          monthly: 'month',
          daily: 'day'
        };
        return modeMap[mode.toLowerCase()] || mode;
      };
  
      const divisionAmounts: Record<string, number> = {};
      if (skuDetails) {
        const divisionMatches = Array.from(skuDetails.matchAll(/(\w+)={totalAmount=([^,]+),skuCode=\[([^\]]+)\]/g));
        for (const match of divisionMatches) {
          divisionAmounts[match[1]] = parseFloat(match[2]);
        }
      }
  
      const errors = errorLines.map(line => {
        const isFrequencyLimit = line.includes('Frequency limit exceeded');
        const mainParts = line.split(' for division:');
        const rest = mainParts[1] || '';
  
        if (isFrequencyLimit) {
          const [division, ...limitParts] = rest.split(',');
          const maxLimit = limitParts.find(p => p.includes('maximum limit allowed'))?.split(':')[1] || '';
          const currentCount = limitParts.find(p => p.includes('current request count'))?.split(':')[1] || '';
          const aggregationMode = limitParts.find(p => p.includes('aggregationMode'))?.split(':')[1] || '';
  
          return {
            type: 'frequency',
            division: division.trim(),
            maxLimit,
            currentCount,
            aggregationMode
          };
        } else {
          const [division, ...budgetParts] = rest.split(',');
          const maxLimit = budgetParts.find(p => p.includes('max limit'))?.split(':')[1] || '';
          const remainingBudget = budgetParts.find(p => p.includes('remaining budget'))?.split(':')[1] || '';
          const attemptedAmount = divisionAmounts[division.trim()];
  
          return {
            type: 'budget',
            division: division.trim(),
            maxLimit,
            remainingBudget,
            attemptedAmount: attemptedAmount.toString()
          };
        }
      });
  
      const skuMap: Record<string, string[]> = {};
      const nonEligible: string[] = [];
      if (skuDetails) {
        const divisionMatches = Array.from(skuDetails.matchAll(/(\w+)={totalAmount=[^,]+,skuCode=\[([^\]]+)\]/g));
        for (const match of divisionMatches) {
          skuMap[match[1]] = Array.from(new Set(match[2].split(',').map(sku => sku.trim())));
        }
      }
  
      errors.forEach(error => {
        const skus = skuMap[error.division] || [];
        nonEligible.push(...skus);
    });
    dispatch(setNonEligible(nonEligible));
      return (
        <div className="error-message-container">
          <div className="error-title">Claim Submission Blocked</div>
          {errors.map((error, index) => {
            const skus = skuMap[error.division] || [];
            const displayedSkus = skus.slice(0, 2);
            const remainingSkus = skus.length > 2 ? skus.slice(2) : [];
            
            return (
              <div key={index} className="error-section">
                {error.type === 'frequency' ? (
                  <>
                    <div className="error-main">
                      You have exceeded the claim frequency limit for
                      <span style={{ fontWeight: 550 }}> Division {error.division}</span>
                    </div>
                    <div className="error-limit">
                      <span style={{ fontWeight: 550 }}>Maximum allowed:</span>{' '}
                      {parseFloat(error.maxLimit).toFixed(0)} claim{parseFloat(error.maxLimit) > 1 ? 's' : ''} per {getAggregationDisplay(error.aggregationMode || 'monthly')}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="error-main">
                      You have exceeded the balance claim amount for division{' '}
                      <span style={{ fontWeight: 550 }}>{error.division}</span>
                    </div>
                      <div className="error-limit">
                        <span style={{ fontWeight: 550 }}>Balance Amount:</span>{' '}
                        {currency} {error.remainingBudget ? formatDecimal(parseFloat(error.remainingBudget)) : '0.00'}
                      </div>
                      <div className="error-limit">
                        <span style={{ fontWeight: 550 }}>Attempted Amount:</span>{' '}
                        {currency} {error.attemptedAmount ? formatDecimal(parseFloat(error.attemptedAmount)) : '0.00'}
                      </div>
                    </>
                )}
                {skus.length > 0 && (
                  <div className="error-skus">
                    <span style={{ fontWeight: 550 }}>
                      Non-eligible SKU{skus.length > 1 ? 's' : ''}:
                    </span>{' '}
                    {displayedSkus.join(', ')}
                    {remainingSkus.length > 0 && (
                      <Tooltip
                        title={<RenderTooltipContent items={remainingSkus} title="SKU" />}
                        placement="right"
                        arrow
                        componentsProps={{
                          tooltip: {
                            sx: {
                              backgroundColor: "#FFFFFF !important",
                              color: "#000000 !important",
                              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1) !important",
                              marginLeft: "-28px",
                            },
                          },
                          arrow: {
                            sx: {
                              color: "#FFFFFF !important",
                            },
                          },
                        }}
                      >
                        <span style={{ color: "#2568E5", textDecoration: "underline", marginLeft: "4px" }}>
                          +{remainingSkus.length}
                        </span>
                      </Tooltip>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    } catch (e) {
      console.error('Error formatting message:', e);
      return message;
    }
  };
  
  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const response = await postClaim(postBody);
      const feature = response?.data?.features?.[0] || {};
      const { statusCode, message, claimId } = feature;
      if (statusCode === 412) {
        setErrorMessage(formatErrorMessage(message));
      } else if (statusCode >= 400 && statusCode < 500) {
        setErrorMessage(message || "An error occurred during submission.");
      } else {
        setApprovalModalState(2);
        setSuccessMessage(
          <>
            Your claim ID <span style={{ fontWeight: 600 }}>{claimId}</span>{" "}
            has been successfully submitted for approval.
          </>
        );
        logAnalyticsEvent("claim_submitted", {
          distributor_id: postBody.supplier,
          claim_type: claimType,
          claim_subtype: claimSubtype,
          claim_amount: postBody.claimTotalValue
        });
      }
    } catch (error: any) {
      console.error("Error occurred during claim submission:", error);
  
      const feature = error?.response?.data?.features?.[0] || {};
      const { statusCode, message } = feature;
      setErrorStatusCode(statusCode);
      const claimIdMatch = message?.match(/CLM[^\s,]*/);
      const claimId = claimIdMatch?.[0];
      if (statusCode === 412) {
        setErrorMessage(formatErrorMessage(message));
      } else {
        setErrorMessage(
          <>
            Claim cannot be raised for Claim ID <span style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{claimId || ""}</span>{". "}
            Please check the claim details or contact support if issue persists.
          </>
        );
      }
      logAnalyticsEvent("claim_submission_failed", {
        distributor_id: postBody.supplier,
        claim_type: claimType,
        claim_subtype: claimSubtype,
        error_type: message || error.message
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNavigate = () => {
    setApprovalModalState(1);
    setApprovalModalOpen(false);

    if (isOpenClaimsPage && openClaimsRows && selectedOpenClaimsRow && updateSelectedRowInRows && claimType) {
      if (claimType !== claimTypes[2]) {
        const childItems = claimType === claimTypes[0]
          ? selectedOpenClaimsRow.batchIds || []
          : selectedOpenClaimsRow.invoices || [];
  
        if (childItems.length > 0) {
          const allStatuses = childItems.map((item: any) => item?.status || "");
          let skuStatus = "PARTIALLY_APPROVED";
  
          if (allStatuses.every((status: string) => status === "APPROVED")) {
            skuStatus = "APPROVED";
          } else if (allStatuses.every((status: string) => status === "REJECTED")) {
            skuStatus = "REJECTED";
          }
  
          if (selectedOpenClaimsRow.status !== skuStatus) {
            const updatedCurrentRow = {
              ...selectedOpenClaimsRow,
              status: skuStatus,
            };
            updateSelectedRowInRows(updatedCurrentRow);
          }
        }
      }

      const currentIndex = openClaimsRows.findIndex((row: any) => row.id === selectedOpenClaimsRow.id);
      const nextIndex = currentIndex + 1;

      if (nextIndex < openClaimsRows.length) {
        const nextRow = openClaimsRows[nextIndex];
        dispatch(setSelectedOpenClaimsRow(nextRow));
      } else {
        if (setIsModalOpen) {
          setIsModalOpen(false); 
        }
          dispatch(setSelectedOpenClaimsRow({}));
      }
    }
    else if (lastSegment === "summary") {
      navigate("/dashboard/claims/status");
      dispatch(resetClaimsState());
    }
  };

  const handleErrorClose = () => {
    setApprovalModalState(1);
    setApprovalModalOpen(false);
    setErrorMessage(null);
    setErrorStatusCode(null);
  };

  return (
    <div>
      <Modal
        open={approvalModalOpen}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
      <div className={`approval-modal-container ${errorStatusCode === 412 ? 'error-state' : ''}`}>
      <div className="info-icon">
          <img
              src={approvalModalState === 1 || errorMessage ? InfoIconImgSrc : TickconImgSrc}
              alt="icon"
            />
          </div>
          <div className="modal-content-scrollable">

          <div className="warning-message">            
            {errorMessage
              ? errorMessage
              : approvalModalState === 2
                ? successMessage
                : approvalModalText[approvalModalState]}
          </div>
          </div>
          <div className="yes-no-button-container">
            {approvalModalState === 1 && !errorMessage && (
              <button
                className="button-common-styles button-no"
                onClick={() => setApprovalModalOpen(false)}
              >
                <span className="no-button-text">No</span>
              </button>
            )}
            <button
              className="button-common-styles button-yes"
              style={{ width: approvalModalState === 1 ? "" : "137px" }}
              onClick={() => {
                if (errorMessage) {
                  handleErrorClose();
                } else {
                  approvalModalState === 1 ? handleSubmit() : handleNavigate();
                }
              }}
              disabled={isSubmitting}
            >
              <span className="yes-button-text">
                {errorMessage
                  ? "Continue"
                  : approvalModalState === 1
                  ? "Yes"
                  : "Continue"}
              </span>
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ApprovalModal;